import '../../../utils/dataTable/patchPageOnlySelect';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useListChargePoints } from '../../../app/ApiGen';
import { Connectors } from '../../../components/Connectors';
import { DataErrorHandler } from '../../../components/ErrorHandler';
import { ExportProps } from '../../../components/ExportCsv';
import { TableLink } from '../../../components/Link';
import { ColumnChangerProps, DataTable, SearchField, SearchFieldProps, Workspace } from '../../../design-system';
import {
  chargePointIdColumn,
  connectivityStatusColumn,
  firmwareVersionColumn,
  modelColumn,
  ocppChargePointIdColumn,
  referenceIdColumn,
  serialColumn,
  vendorColumn,
} from '../../../utils/chargePoints/table/columns';
import {
  ConnectivityStatusFilters,
  filterByConnectivityStatus,
} from '../../../utils/chargePoints/table/filterComponents/ConnectivityStatusFilters';
import {
  ConnectorStatusFilters,
  filterByConnectorStatus,
} from '../../../utils/chargePoints/table/filterComponents/ConnectorStatusFilters';
import { LlmModeFilters, type LlmModeOption } from '../../../utils/chargePoints/table/filterComponents/LlmModeFilters';
import {
  MultiTextFilter,
  multiTextFilterSearch,
} from '../../../utils/chargePoints/table/filterComponents/MultiTextFilter';
import type { Column } from '../../../utils/dataTable/columns';
import { getCondensedDate } from '../../../utils/dateFormat';
import { pluralise } from '../../../utils/format';
import { filterBySelectableItem } from '../../../utils/jobs/filterComponents/SelectableFilters';
import { TextFieldFilter } from '../../../utils/jobs/filterComponents/TextFieldFilter';
import { YesNoFilters, type YesNoOption } from '../../../utils/jobs/filterComponents/YesNoFilters';
import { YesNoNAFilters, type YesNoNAOption } from '../../../utils/jobs/filterComponents/YesNoNAFilters';
import { YesNoUnknownFilters, type YesNoUnknownOption } from '../../../utils/jobs/filterComponents/YesNoUnknownFilters';
import { normalise } from '../../../utils/request';
import { sortByConnectors, sortByLocale } from '../../../utils/sortBy';
import { ChargePointsToolbar } from './ChargePointsToolbar';
import { FlexUpgradeDialog } from './FlexUpgradeDialog';
import { constructTableChargePoints, TableChargePoint } from './utils';

type SelectionFooterProps = {
  count: number;
  total: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onReset: () => void;
};

const SelectionFooter: React.FC<SelectionFooterProps> = ({ count, total, onSelectAll, onDeselectAll, onReset }) => (
  <Box p={2} display="flex" alignItems="center" justifyContent="space-between">
    <Typography fontWeight="bold">{pluralise(count, 'charge point')} selected</Typography>
    <Box display="flex" gap={1}>
      {count > 0 ? (
        <Button size="small" variant="outlined" onClick={onDeselectAll}>
          Deselect all ({count})
        </Button>
      ) : (
        <Button size="small" variant="outlined" onClick={onSelectAll}>
          Select all ({total})
        </Button>
      )}
      <Button size="small" variant="outlined" color="inherit" onClick={onReset}>
        Reset
      </Button>
    </Box>
  </Box>
);

export const ChargePointsList: React.FC = () => {
  const { loading, error, data: chargePointsData, refetch } = useListChargePoints({});

  const handleRefresh = () => {
    refetch({ queryParams: { forceRefresh: true } }).catch(() => {
      console.log('Error refreshing charge points');
    });
  };
  const cacheInfo = chargePointsData?.cache as { lastUpdated?: string } | undefined;

  const [filtering, setFiltering] = React.useState<boolean>(true);
  const [tableChargePoints, setTableChargePoints] = React.useState<TableChargePoint[]>([]);
  const [selectedChargePointsCount, setSelectedChargePointsCount] = React.useState<number>(0);
  const [flexUpgradeOpen, setFlexUpgradeOpen] = React.useState<boolean>(false);
  const [selectAllVersion, setSelectAllVersion] = React.useState<number>(0);
  // Bumping this state re-renders the component without changing DataTable's data prop ref,
  // so material-table picks up our in-place `tableData.checked` mutations without running its
  // componentDidUpdate side-effects (which would clear column filter state).
  const [, setRenderTrigger] = React.useState<number>(0);
  // Holds the rows material-table is currently showing (column filter + search applied).
  // Select / Deselect all target this set so a filtered view doesn't silently touch hidden
  // rows. Kept in a ref (not state) so the footer count is always up-to-date without
  // scheduling renders that would interrupt the user typing in a filter input.
  const visibleRowsRef = React.useRef<TableChargePoint[]>([]);

  const columns = React.useMemo<Column<TableChargePoint>[]>(
    () => [
      {
        title: 'Name',
        field: 'name',
        defaultSort: 'asc',
        customSort: sortByLocale('name'),
        filterComponent: MultiTextFilter,
        customFilterAndSearch: (search: unknown, { name }) => multiTextFilterSearch(search, name),
        render: ({ id, name, organisationSlug }) =>
          organisationSlug ? (
            <TableLink to={`/organisations/${organisationSlug}/charge-points/${id}`}>{name}</TableLink>
          ) : (
            name
          ),
      },
      {
        title: 'Pages',
        sorting: false,
        filtering: false,
        export: false,
        render: ({ id, organisationSlug }) => {
          if (!organisationSlug) return <></>;
          const base = `/organisations/${organisationSlug}/charge-points/${id}`;
          const links: [string, string][] = [
            ['Diagnostics', `${base}/diagnostics`],
            ['Sessions', `${base}/sessions`],
            ['Config', `${base}/charging-configuration`],
          ];
          return (
            <Box display="flex" gap={0.5} flexWrap="nowrap">
              {links.map(([label, href]) => (
                <Button
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener"
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: 'none', minWidth: 0, py: 0, px: 1, fontSize: '0.75rem' }}
                >
                  {label}
                </Button>
              ))}
            </Box>
          );
        },
      },
      {
        title: 'Organisation',
        field: 'organisationName',
        customSort: sortByLocale('organisationName'),
        filterComponent: MultiTextFilter,
        customFilterAndSearch: (search: unknown, { organisationName }) =>
          multiTextFilterSearch(search, organisationName),
        render: ({ organisationName, organisationSlug }) =>
          organisationSlug ? (
            <TableLink to={`/organisations/${organisationSlug}`}>{organisationName}</TableLink>
          ) : (
            organisationName
          ),
      },
      {
        ...connectivityStatusColumn<TableChargePoint>(),
        filterComponent: ConnectivityStatusFilters,
        customFilterAndSearch: (search: unknown, rowData) => filterByConnectivityStatus(search, rowData),
      },
      {
        ...referenceIdColumn<TableChargePoint>(),
        filterComponent: MultiTextFilter,
        customFilterAndSearch: (search: unknown, { referenceId }) => multiTextFilterSearch(search, referenceId),
      },
      {
        ...ocppChargePointIdColumn<TableChargePoint>(),
        filterComponent: MultiTextFilter,
        customFilterAndSearch: (search: unknown, { ocppChargePointId }) =>
          multiTextFilterSearch(search, ocppChargePointId),
      },
      {
        ...chargePointIdColumn<TableChargePoint>(),
        filterComponent: MultiTextFilter,
        customFilterAndSearch: (search: unknown, { id }) => multiTextFilterSearch(search, id),
      },
      {
        ...serialColumn<TableChargePoint>(),
        filterComponent: MultiTextFilter,
        customFilterAndSearch: (search: unknown, { serial }) => multiTextFilterSearch(search, serial),
      },
      {
        ...modelColumn<TableChargePoint>(),
        filterComponent: MultiTextFilter,
        customFilterAndSearch: (search: unknown, { model }) => multiTextFilterSearch(search, model),
      },
      {
        ...vendorColumn<TableChargePoint>(),
        filterComponent: MultiTextFilter,
        customFilterAndSearch: (search: unknown, { vendor }) => multiTextFilterSearch(search, vendor),
      },
      {
        ...firmwareVersionColumn<TableChargePoint>(),
        filterComponent: MultiTextFilter,
        customFilterAndSearch: (search: unknown, { firmware }) => multiTextFilterSearch(search, firmware),
      },
      {
        title: 'Flex Upgraded',
        field: 'flexSolarUnlocked',
        hidden: true,
        filterComponent: YesNoNAFilters,
        customFilterAndSearch: (search: unknown, { flexSolarUnlocked }) =>
          filterBySelectableItem<YesNoNAOption>(search, { value: flexSolarUnlocked as YesNoNAOption }),
      },
      {
        title: 'Long term offline charging',
        field: 'allowLongTermOfflineCharging',
        hidden: true,
        filterComponent: YesNoUnknownFilters,
        customFilterAndSearch: (search: unknown, { allowLongTermOfflineCharging }) =>
          filterBySelectableItem<YesNoUnknownOption>(search, {
            value: allowLongTermOfflineCharging as YesNoUnknownOption,
          }),
      },
      {
        title: 'Connectors',
        field: 'connectorStatuses',
        render: ({ connectors, networkStatus }) => <Connectors connectors={connectors} networkStatus={networkStatus} />,
        filterComponent: ConnectorStatusFilters,
        customFilterAndSearch: (search: unknown, item) => filterByConnectorStatus(search, item),
        customSort: sortByConnectors,
      },
      {
        title: 'Active this month',
        field: 'activeThisMonth',
        hidden: true,
        filterComponent: YesNoFilters,
        customFilterAndSearch: (search: unknown, { activeThisMonth }) =>
          filterBySelectableItem<YesNoOption>(search, { value: activeThisMonth as YesNoOption }),
      },
      {
        title: 'ICCID',
        field: 'iccid',
        hidden: true,
        filterComponent: MultiTextFilter,
        customFilterAndSearch: (search: unknown, { iccid }) => multiTextFilterSearch(search, iccid),
      },
      {
        title: 'LLM Mode',
        field: 'localLoadManagementMode',
        hidden: true,
        filterComponent: LlmModeFilters,
        customFilterAndSearch: (search: unknown, { localLoadManagementMode }) =>
          filterBySelectableItem<LlmModeOption>(search, { value: localLoadManagementMode as LlmModeOption }),
      },
      {
        title: 'Created',
        render: ({ createdDate }) => getCondensedDate(createdDate),
        field: 'createdDate',
        hidden: true,
        filterComponent: TextFieldFilter,
      },
      {
        title: 'Updated',
        field: 'updatedDate',
        render: ({ updatedDate }) => getCondensedDate(updatedDate),
        hidden: true,
        filterComponent: TextFieldFilter,
      },
    ],
    [],
  );

  React.useEffect(() => {
    if (chargePointsData) {
      const chargePoints = normalise(chargePointsData);
      setTableChargePoints(constructTableChargePoints(chargePoints));
      // New row objects from constructTableChargePoints carry no `checked` flags, so any prior
      // selection count is stale and would leave the footer/bulk-update buttons enabled.
      setSelectedChargePointsCount(0);
    }
  }, [chargePointsData]);

  // material-table holds row references internally and reads `tableData.checked` on render.
  // Mutating in place + bumping renderTrigger (NOT the data prop) re-renders the checkboxes
  // without running material-table's componentDidUpdate path that would reset filter state.
  const handleSelectAll = React.useCallback(() => {
    const visible = visibleRowsRef.current;
    visible.forEach((cp) => {
      // eslint-disable-next-line no-param-reassign
      cp.tableData.checked = true;
    });
    setSelectedChargePointsCount(visible.length);
    setRenderTrigger((v) => v + 1);
  }, []);

  const handleDeselectAll = React.useCallback(() => {
    visibleRowsRef.current.forEach((cp) => {
      // eslint-disable-next-line no-param-reassign
      cp.tableData.checked = false;
    });
    setSelectedChargePointsCount(0);
    setRenderTrigger((v) => v + 1);
  }, []);

  // Reset clears column filter inputs and selection by remounting the table.
  const handleReset = React.useCallback(() => {
    columns.forEach((col) => {
      if (col.tableData) {
        // eslint-disable-next-line no-param-reassign
        col.tableData.filterValue = undefined;
      }
    });
    tableChargePoints.forEach((cp) => {
      // eslint-disable-next-line no-param-reassign
      cp.tableData.checked = false;
    });
    setSelectedChargePointsCount(0);
    setSelectAllVersion((v) => v + 1);
  }, [columns, tableChargePoints]);

  if (error) {
    return <DataErrorHandler description="Unable to load charge points" error={error} refetch={refetch} />;
  }

  return (
    <Workspace maxWidth="xl">
      <Card>
        <DataTable<TableChargePoint>
          // Remount to force material-table to re-mirror tableChargePoints on reset.
          // material-table clones rows on mount and does not sync internal state
          // from prop changes, so without a remount the checkboxes stay stale.
          key={selectAllVersion}
          options={{ columnsButton: true, filtering, selection: true }}
          toolbarProps={{
            search: ({ dataManager, search, onSearchChanged }: SearchFieldProps) => (
              <SearchField
                dataManager={dataManager}
                item="charge points"
                onSearchChanged={onSearchChanged}
                search={search}
              />
            ),
            actions: (props: ColumnChangerProps & ExportProps<TableChargePoint>) => {
              // props.data is the rows material-table is currently showing (column filter +
              // search applied). Stash it so Select/Deselect-all operate on the visible set.
              visibleRowsRef.current = Array.isArray(props.data) ? (props.data as TableChargePoint[]) : [];
              return (
                <ChargePointsToolbar
                  tableProps={props}
                  filtering={filtering}
                  onFilteringChange={setFiltering}
                  onRefresh={handleRefresh}
                  isLoading={loading}
                  cacheInfo={cacheInfo}
                  allChargePoints={tableChargePoints}
                  selectedCount={selectedChargePointsCount}
                  onFlexUpgrade={() => setFlexUpgradeOpen(true)}
                />
              );
            },
          }}
          onSelectionChange={(selected) => setSelectedChargePointsCount(selected.length)}
          columns={columns}
          data={tableChargePoints}
        />
        <SelectionFooter
          count={selectedChargePointsCount}
          total={tableChargePoints.length}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          onReset={handleReset}
        />
      </Card>
      <FlexUpgradeDialog
        isOpen={flexUpgradeOpen}
        onClose={() => setFlexUpgradeOpen(false)}
        chargePoints={tableChargePoints.filter((cp) => cp.tableData.checked)}
      />
    </Workspace>
  );
};
